import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/meetingsApi";
import type { MeetingWritePayload, NotePhase } from "../types";

export const qk = {
  meetings: (params?: Record<string, string>) => ["meetings", params ?? {}] as const,
  meeting: (id: string) => ["meetings", "detail", id] as const,
  occurrences: (params: Record<string, string | undefined>) =>
    ["meetings", "occurrences", params] as const,
  occurrence: (id: string) => ["meetings", "occurrence", id] as const,
  notes: (occurrenceId: string) => ["meetings", "notes", occurrenceId] as const,
  guestInvites: (occurrenceId: string) =>
    ["meetings", "guest-invites", occurrenceId] as const,
};

export const useMeetings = (params?: Record<string, string>) =>
  useQuery({ queryKey: qk.meetings(params), queryFn: () => api.listMeetings(params) });

export const useMeeting = (id: string | undefined) =>
  useQuery({
    queryKey: qk.meeting(id ?? ""),
    queryFn: () => api.getMeeting(id as string),
    enabled: !!id,
  });

export const useOccurrencesRange = (params: {
  from?: string;
  to?: string;
  status?: string;
  ze_project?: string;
}) =>
  useQuery({
    queryKey: qk.occurrences(params),
    queryFn: () => api.listOccurrences(params),
  });

export const useOccurrence = (id: string | undefined) =>
  useQuery({
    queryKey: qk.occurrence(id ?? ""),
    queryFn: () => api.getOccurrence(id as string),
    enabled: !!id,
    refetchInterval: 30_000, // status/attendance drift while the page is open
  });

function useInvalidateMeetings() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["meetings"] });
}

export function useCreateMeeting() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (payload: MeetingWritePayload) => api.createMeeting(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateMeeting(id: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (payload: Partial<MeetingWritePayload>) =>
      api.updateMeeting(id, payload),
    onSuccess: invalidate,
  });
}

export function useCancelMeeting() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (id: string) => api.cancelMeeting(id),
    onSuccess: invalidate,
  });
}

export function useRespond(meetingId: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (response: "accepted" | "declined") =>
      api.respondToMeeting(meetingId, response),
    onSuccess: invalidate,
  });
}

export function useInviteToMeeting(meetingId: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (participantIds: string[]) =>
      api.inviteToMeeting(meetingId, participantIds),
    onSuccess: invalidate,
  });
}

export function useToggleGuestNotes(meetingId: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (enabled: boolean) => api.toggleGuestNotes(meetingId, enabled),
    onSuccess: invalidate,
  });
}

export function useOccurrenceAction(
  action: "start" | "end" | "cancel",
) {
  const invalidate = useInvalidateMeetings();
  const fn =
    action === "start"
      ? api.startOccurrence
      : action === "end"
        ? api.endOccurrence
        : api.cancelOccurrence;
  return useMutation({ mutationFn: (id: string) => fn(id), onSuccess: invalidate });
}

export const useOccurrenceNotes = (occurrenceId: string | undefined) =>
  useQuery({
    queryKey: qk.notes(occurrenceId ?? ""),
    queryFn: () => api.listNotes(occurrenceId as string),
    enabled: !!occurrenceId,
  });

export function useAddNote(occurrenceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { phase: NotePhase; content: string }) =>
      api.addNote(occurrenceId, payload),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: qk.notes(occurrenceId) }),
  });
}

export function useUpdateNote(occurrenceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) =>
      api.updateNote(noteId, { content }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: qk.notes(occurrenceId) }),
  });
}

export function useDeleteNote(occurrenceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => api.deleteNote(noteId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: qk.notes(occurrenceId) }),
  });
}

export const useGuestInvites = (occurrenceId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.guestInvites(occurrenceId ?? ""),
    queryFn: () => api.listGuestInvites(occurrenceId as string),
    enabled: enabled && !!occurrenceId,
  });

export function useCreateGuestInvite(occurrenceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { display_name?: string; email?: string }) =>
      api.createGuestInvite(occurrenceId, payload),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: qk.guestInvites(occurrenceId) }),
  });
}

export function useRevokeGuestInvite(occurrenceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.revokeGuestInvite(inviteId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: qk.guestInvites(occurrenceId) }),
  });
}
